import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const privacyPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy | Everything Automation',
  description: 'Read the Everything Automation privacy policy. We are committed to protecting your personal data under UK GDPR.',
  url: `${siteUrl}/privacy-policy`,
  publisher: {
    '@type': 'Organization',
    name: 'Everything Automation',
    url: siteUrl,
  },
  dateModified: '2026-03-18',
};

const sections = [
  {
    id: 'who-we-are',
    title: '1. Who We Are',
    content: `Everything Automation is a UK-based AI automation company that provides AI chatbot, booking automation, CRM integration, lead management, email automation, and WhatsApp automation services to businesses.

Company name: Everything Automation
Email: sales@everythingautomation.co
Phone: +44 7450 946360
Website: everythingautomation.co

We are committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, store, and share your personal information when you visit our website or use our services.`,
  },
  {
    id: 'data-we-collect',
    title: '2. Data We Collect',
    content: `We may collect and process the following types of personal data:

Identity & Contact Data
- Full name
- Email address
- Phone number
- Business name and job title

Usage Data
- IP address
- Browser type and version
- Pages visited and time spent on our website
- Referral source (how you found us)

Communications Data
- Messages submitted via our contact form or chatbot
- Email correspondence with our team
- Reviews or feedback you submit

We collect this data when you fill in a form on our website, contact us directly, book a demo call, or interact with our AI chatbot widget.`,
  },
  {
    id: 'how-we-use-it',
    title: '3. How We Use Your Data',
    content: `We use your personal data for the following purposes:

- To respond to enquiries and provide quotes
- To deliver and manage our AI automation services
- To schedule and conduct demo calls
- To send service-related communications (confirmations, updates)
- To improve our website, products, and customer experience
- To comply with legal obligations

We do not use your data for unsolicited marketing without your consent. If you opt in to marketing communications, you can unsubscribe at any time.

Legal basis (UK GDPR): We process your data under legitimate interest (responding to enquiries), contract performance (delivering services), your consent (marketing), and legal obligation where required.`,
  },
  {
    id: 'data-sharing',
    title: '4. Data Sharing & Third Parties',
    content: `We do not sell your personal data to third parties. We may share your data with:

- Service providers: Trusted tools we use to operate our business (e.g. email platforms, CRM systems, scheduling software). These are bound by data processing agreements.
- Legal authorities: If required by law, court order, or regulatory authority.
- Business transfers: In the event of a merger, acquisition, or sale of our business assets, your data may be transferred to the relevant party.

All third-party processors are required to handle your data securely and only for the purposes we instruct.`,
  },
  {
    id: 'cookies',
    title: '5. Cookies',
    content: `Our website uses cookies to improve your browsing experience. Cookies are small text files stored on your device.

Types of cookies we use:
- Essential cookies: Required for the website to function properly
- Analytics cookies: Help us understand how visitors use our site (e.g. page views, time on site)
- Preference cookies: Remember your settings and choices

You can control cookie settings through your browser. Note that disabling certain cookies may affect the functionality of our website. By continuing to use our website, you consent to our use of cookies as described above.`,
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    content: `We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law.

- Enquiry and contact data: Up to 3 years from last contact
- Client data: For the duration of our contract plus 6 years (for legal/accounting purposes)
- Website analytics: Up to 26 months

After these periods, your data is securely deleted or anonymised.`,
  },
  {
    id: 'your-rights',
    title: '7. Your Rights (UK GDPR)',
    content: `Under UK data protection law, you have the following rights:

- Right to access: Request a copy of the personal data we hold about you
- Right to rectification: Ask us to correct inaccurate or incomplete data
- Right to erasure: Request deletion of your data (the "right to be forgotten")
- Right to restrict processing: Ask us to limit how we use your data
- Right to data portability: Receive your data in a structured, machine-readable format
- Right to object: Object to processing based on legitimate interest or for direct marketing
- Rights related to automated decisions: Not to be subject to solely automated decisions that affect you significantly

To exercise any of these rights, please contact us at sales@everythingautomation.co. We will respond within 30 days. You also have the right to lodge a complaint with the Information Commissioner\'s Office (ICO) at ico.org.uk.`,
  },
  {
    id: 'data-security',
    title: '8. Data Security',
    content: `We take the security of your personal data seriously. We implement appropriate technical and organisational measures to protect your data against unauthorised access, loss, destruction, or alteration.

These measures include:
- Secure encrypted connections (HTTPS) on our website
- Access controls limiting who can view personal data
- Regular review of our data handling practices

While we take every reasonable precaution, no method of internet transmission is 100% secure. We cannot guarantee absolute security but we continuously work to improve our safeguards.`,
  },
  {
    id: 'third-party-links',
    title: '9. Third-Party Links',
    content: `Our website may contain links to external websites. We are not responsible for the privacy practices or content of those sites. We encourage you to read the privacy policies of any third-party sites you visit.`,
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The latest version will always be available on this page with the effective date shown below. We encourage you to review this policy periodically.`,
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    content: `If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please contact us:

Email: sales@everythingautomation.co
Phone: +44 7450 946360
WhatsApp: +44 7450 946360

We aim to respond to all data-related queries within 5 business days.`,
  },
];

const PrivacyPage = () => {
  return (
    <>
      <title>Privacy Policy | Everything Automation</title>
      <meta name="description" content="Read the Everything Automation privacy policy. We explain how we collect, use, and protect your personal data in compliance with UK GDPR. Your privacy is our priority." />
      <meta name="keywords" content="Everything Automation privacy policy, data protection, UK GDPR compliance" />
      <link rel="canonical" href={`${siteUrl}/privacy-policy`} />
      <meta property="og:title" content="Privacy Policy | Everything Automation" />
      <meta property="og:description" content="How Everything Automation collects, uses, and protects your personal data. UK GDPR compliant." />
      <meta property="og:url" content={`${siteUrl}/privacy-policy`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(privacyPageSchema) }} />
      <main className="bg-ea-black min-h-screen">
        {/* Hero */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-ea-blue/5 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
              <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Legal</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-ea-gray text-base leading-relaxed max-w-2xl">
              We take your privacy seriously. This policy explains what data we collect, how we use it, and your rights as a data subject under UK GDPR.
            </p>
            <p className="text-ea-gray/60 text-sm mt-4">
              Last updated: <span className="text-ea-gray">18 March 2026</span>
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 pb-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            {/* Quick Nav */}
            <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-6 mb-10">
              <p className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Contents</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-ea-gray hover:text-ea-blue text-sm transition-colors duration-200 cursor-pointer flex items-center gap-2"
                    rel="nofollow"
                  >
                    <i className="ri-arrow-right-s-line text-ea-blue text-xs"></i>
                    {s.title}
                  </a>
                ))}
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {sections.map((s) => (
                <div
                  key={s.id}
                  id={s.id}
                  className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-8 scroll-mt-24"
                >
                  <h4 className="text-white font-bold text-lg mb-5 flex items-center gap-3">
                    <span className="w-1 h-5 bg-ea-blue rounded-full inline-block shrink-0"></span>
                    <a id={s.id} className="hover:text-ea-blue transition-colors">{s.title}</a>
                  </h4>
                  <div className="text-ea-gray text-sm leading-relaxed whitespace-pre-line">
                    {s.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Back & Contact */}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-ea-dark-border">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-ea-gray hover:text-white text-sm transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold text-sm rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Questions? Contact Us <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default PrivacyPage;
